import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import { env } from '@/config/env'
import { ENDPOINTS } from '@/constants/api'
import { ALL_QUERY_KEYS } from '@/constants/query-keys'
import { useAuthStore } from '@/store/auth.store'

/**
 * Base query for all tap-api calls. Injects the in-memory access token as a
 * Bearer header, and the deployment tenant id as X-Tenant-ID for pre-auth flows
 * (tap-api resolves the tenant from this header before a session exists).
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: env.VITE_API_URL,
  prepareHeaders: (headers) => {
    const token = useAuthStore.getState().accessToken
    if (token) headers.set('authorization', `Bearer ${token}`)
    if (env.VITE_TENANT_ID) headers.set('x-tenant-id', env.VITE_TENANT_ID)
    return headers
  },
})

/** Refresh response data envelope (mirrors tap-api TokenPair). */
interface RefreshEnvelope {
  data: { accessToken: string; refreshToken: string }
}

function isRefreshEnvelope(value: unknown): value is RefreshEnvelope {
  if (typeof value !== 'object' || value === null || !('data' in value)) return false
  const data = value.data
  return (
    typeof data === 'object' &&
    data !== null &&
    'accessToken' in data &&
    'refreshToken' in data &&
    typeof data.accessToken === 'string' &&
    typeof data.refreshToken === 'string'
  )
}

// Serialise concurrent refreshes: if many requests 401 at once, only one hits
// /auth/refresh; the rest await the same promise.
let refreshPromise: Promise<string | null> | null = null

/**
 * Wraps the base query with transparent re-authentication:
 *   401 → POST /auth/refresh { refreshToken } → rotate tokens → retry original
 *   → on failure, clear the session (forces redirect to /login).
 */
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await rawBaseQuery(args, api, extraOptions)

  if (result.error?.status !== 401) return result

  const requestUrl = typeof args === 'string' ? args : args.url
  // A failed refresh itself must not recurse into another refresh.
  if (requestUrl === ENDPOINTS.AUTH.REFRESH) {
    useAuthStore.getState().clearSession()
    return result
  }

  const storedRefreshToken = useAuthStore.getState().refreshToken
  if (!storedRefreshToken) {
    useAuthStore.getState().clearSession()
    return result
  }

  refreshPromise ??= (async () => {
    const refresh = await rawBaseQuery(
      { url: ENDPOINTS.AUTH.REFRESH, method: 'POST', body: { refreshToken: storedRefreshToken } },
      api,
      extraOptions,
    )
    if (refresh.data && isRefreshEnvelope(refresh.data)) {
      const { accessToken, refreshToken } = refresh.data.data
      useAuthStore.getState().setTokens({ accessToken, refreshToken })
      return accessToken
    }
    useAuthStore.getState().clearSession()
    return null
  })()

  const newToken = await refreshPromise
  refreshPromise = null

  if (newToken) {
    // prepareHeaders re-reads the (now refreshed) token from the store.
    result = await rawBaseQuery(args, api, extraOptions)
  }

  return result
}

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ALL_QUERY_KEYS,
  endpoints: () => ({}),
})
